import type { PathCurveTo, PathCommand as StowedPathCommand } from '@/api/types/stowed-element'
import type { PathCommand } from '@/lib/elements/types/path-command'

/**
 * Converts in-memory `PathCommand`s (top-left-relative, 0..width × 0..height — same convention
 * `shapeToPathCommands`/`ShapeRenderer` use) to the wire's center-relative convention, matching
 * legacy `page-editor`'s own save code exactly (`ODPath.prototype.addSegmentToPXML`,
 * odeditorsvg.js:15091 — `X = seg.point.x - rect.width/2`, `Y = seg.point.y - rect.height/2`,
 * same subtraction for `CX`/`CY`/`DX`/`DY`). Legacy's *load* side (`ODXMLUtil.getPath`) adds
 * `width/2`/`height/2` back to reconstruct its own top-left-relative internal points — so writing
 * our already-top-left-relative coordinates unshifted double-applies that shift on legacy's next
 * load, displacing every point in the path by a constant (width/2, height/2) offset. Invisible for
 * a plain rectangle outline (shapeToPathCommands isn't used for kindId 1), but immediately visible
 * for any irregular polygon or pen path with explicit coordinates — e.g. a heart shape rendering
 * as a displaced, arbitrarily-clipped fragment instead of a heart.
 */
export function convertPathCommands(
  commands: PathCommand[],
  width: number,
  height: number
): StowedPathCommand[] {
  const cx = width / 2
  const cy = height / 2

  return commands.map((cmd) => {
    if ('moveTo' in cmd) return { moveTo: { x: cmd.moveTo.x - cx, y: cmd.moveTo.y - cy } }
    if ('lineTo' in cmd) return { lineTo: { x: cmd.lineTo.x - cx, y: cmd.lineTo.y - cy } }
    if ('curveTo' in cmd) {
      const ct = cmd.curveTo
      const curveTo: PathCurveTo = {
        controlX: ct.controlX - cx,
        controlY: ct.controlY - cy,
        x: ct.x - cx,
        y: ct.y - cy,
      }

      // Second control: in-memory commands use controlX2 (editor) or deltaX (after API load).
      // Must always serialize to protobuf deltaX/deltaY or cubics lose their second point on save.
      const c2x =
        typeof ct.controlX2 === 'number'
          ? ct.controlX2
          : typeof ct.deltaX === 'number'
            ? ct.deltaX
            : undefined
      const c2y =
        typeof ct.controlY2 === 'number'
          ? ct.controlY2
          : typeof ct.deltaY === 'number'
            ? ct.deltaY
            : undefined

      if (typeof c2x === 'number' && typeof c2y === 'number') {
        curveTo.deltaX = c2x - cx
        curveTo.deltaY = c2y - cy
      }

      return { curveTo }
    }

    return {}
  })
}
